import React, {useEffect, useState, useRef} from "react";
import {
  Box,
  Paper,
  Typography,
  InputLabel,
  FormHelperText,
  Grid,
  Alert,
  Snackbar,
  CircularProgress
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import properties from "constants/ApplicationConstants";
import {POST, GET} from "APIclient";
import {useNavigate} from "react-router-dom";

const ExtendKra = ({title = "Change The Dates To Extend KRA"}) => {
  const navigate = useNavigate();

  const [evalStartDate, setEvalStartDate] = useState(null);
  const [evalEndDate, setEvalEndDate] = useState(null);
  const [formEditDeadline, setFormEditDeadline] = useState(null);
  const [employeeKraStartDate, setEmployeeKraStartDate] = useState(null);
  const [employeeKraEndDate, setEmployeeKraEndDate] = useState(null);
  const [managerKraStartDate, setManagerKraStartDate] = useState(null);
  const [managerKraEndDate, setManagerKraEndDate] = useState(null);

  const originalValues = useRef(null);

  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackColor, setSnackColor] = useState("success");
  const [snackMessage, setSnackMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    const fetchCurrentKraDates = async () => {
      setIsLoadingData(true);
      try {
        const url = "admin/appraisal-cycle/last";
        const response = await GET(url);
        if (response?.status === 200) {
          const data = response?.data?.data;

          const fetched = {
            evalStartDate: data?.evalutionStartDate ? dayjs(data.evalutionStartDate) : null,
            evalEndDate: data?.evalutionEndDate ? dayjs(data.evalutionEndDate) : null,
            formEditDeadline: data?.formEditDeadline ? dayjs(data.formEditDeadline) : null,
            employeeKraStartDate: data?.employeeKraStartDate
              ? dayjs(data.employeeKraStartDate)
              : null,
            employeeKraEndDate: data?.employeeKraEndDate ? dayjs(data.employeeKraEndDate) : null,
            managerKraStartDate: data?.managerKraStartDate ? dayjs(data.managerKraStartDate) : null,
            managerKraEndDate: data?.managerKraEndDate ? dayjs(data.managerKraEndDate) : null
          };

          originalValues.current = fetched;

          setEvalStartDate(fetched.evalStartDate);
          setEvalEndDate(fetched.evalEndDate);
          setFormEditDeadline(fetched.formEditDeadline);
          setEmployeeKraStartDate(fetched.employeeKraStartDate);
          setEmployeeKraEndDate(fetched.employeeKraEndDate);
          setManagerKraStartDate(fetched.managerKraStartDate);
          setManagerKraEndDate(fetched.managerKraEndDate);
        }
      } catch (error) {
        showSnack(error?.response?.data?.message || "Failed to load current KRA dates", "error");
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchCurrentKraDates();
  }, []);

  const hasChanges = (current, fieldName) => {
    if (!originalValues.current) return false;
    const original = originalValues.current[fieldName];
    if (!original && !current) return false;
    if (!original || !current) return true;
    return !original.isSame(current, "day");
  };

  useEffect(() => {
    if (!originalValues.current) return;

    const anyChanged =
      hasChanges(evalStartDate, "evalStartDate") ||
      hasChanges(evalEndDate, "evalEndDate") ||
      hasChanges(formEditDeadline, "formEditDeadline") ||
      hasChanges(employeeKraStartDate, "employeeKraStartDate") ||
      hasChanges(employeeKraEndDate, "employeeKraEndDate") ||
      hasChanges(managerKraStartDate, "managerKraStartDate") ||
      hasChanges(managerKraEndDate, "managerKraEndDate");

    const allFilled =
      evalStartDate &&
      evalEndDate &&
      formEditDeadline &&
      employeeKraStartDate &&
      employeeKraEndDate &&
      managerKraStartDate &&
      managerKraEndDate;

    setIsButtonDisabled(!(anyChanged && allFilled));
  }, [
    evalStartDate,
    evalEndDate,
    formEditDeadline,
    employeeKraStartDate,
    employeeKraEndDate,
    managerKraStartDate,
    managerKraEndDate
  ]);

  const showSnack = (message, color = "success") => {
    setSnackMessage(message);
    setSnackColor(color);
    setSnackOpen(true);
  };

  const handleCloseSnack = () => setSnackOpen(false);

  const validate = () => {
    const newErrors = {};
    if (!evalStartDate) newErrors.eval_start_date = "Start date is required";
    if (!evalEndDate) newErrors.eval_end_date = "End date is required";
    if (!formEditDeadline) newErrors.form_edit_deadline = "This field is required";
    if (!employeeKraStartDate) newErrors.employee_kra_start_date = "Start date is required";
    if (!employeeKraEndDate) newErrors.employee_kra_end_date = "End date is required";
    if (!managerKraStartDate) newErrors.manager_kra_start_date = "Start date is required";
    if (!managerKraEndDate) newErrors.manager_kra_end_date = "End date is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const payload = {
      evalutionStartDate: evalStartDate.format("YYYY-MM-DD"),
      evalutionEndDate: evalEndDate.format("YYYY-MM-DD"),
      formEditDeadline: formEditDeadline.format("YYYY-MM-DD"),
      employeeKraStartDate: employeeKraStartDate.format("YYYY-MM-DD"),
      employeeKraEndDate: employeeKraEndDate.format("YYYY-MM-DD"),
      managerKraStartDate: managerKraStartDate.format("YYYY-MM-DD"),
      managerKraEndDate: managerKraEndDate.format("YYYY-MM-DD")
    };

    try {
      const url = "admin/appraisal-cycle/extend-kra";
      const response = await POST(url, payload);
      if (response?.status === 200) {
        showSnack(response?.data?.message || "KRA dates extended successfully!");
        originalValues.current = {
          evalStartDate,
          evalEndDate,
          formEditDeadline,
          employeeKraStartDate,
          employeeKraEndDate,
          managerKraStartDate,
          managerKraEndDate
        };
        setIsButtonDisabled(true);
      }
    } catch (error) {
      showSnack(error?.response?.data?.message || "Failed to extend KRA dates", "error");
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
          <CircularProgress color="info" size={50} />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{flexGrow: 1, p: 2}}>
            <Paper elevation={3}>
              <Box sx={{background: properties.colors.bgcolorbluethem, color: "white", p: 2}}>
                <Typography
                  variant="h6"
                  fontSize={15}
                  sx={{color: properties.colors.White, fontFamily: properties.fontFamily}}
                >
                  Edit Evaluation Dates
                </Typography>
              </Box>

              <Box sx={{p: 2}}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <InputLabel
                      sx={{fontWeight: "bold", fontSize: "13", fontFamily: properties.fontFamily}}
                    >
                      Evaluation Date <span style={{color: "red"}}>*</span>
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      value={evalStartDate}
                      format="DD-MM-YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.eval_start_date,
                          helperText: errors.eval_start_date
                        }
                      }}
                      onChange={newValue => {
                        setEvalStartDate(newValue);
                        setErrors(prev => ({...prev, eval_start_date: null}));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      value={evalEndDate}
                      format="DD-MM-YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.eval_end_date,
                          helperText: errors.eval_end_date
                        }
                      }}
                      onChange={newValue => {
                        setEvalEndDate(newValue);
                        setErrors(prev => ({...prev, eval_end_date: null}));
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{background: properties.colors.bgcolorbluethem, color: "white", p: 2, mt: 2}}
              >
                <Typography
                  variant="h6"
                  fontSize={15}
                  sx={{color: properties.colors.White, fontFamily: properties.fontFamily}}
                >
                  Edit KRA Details
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Box sx={{p: 3}}>
                  <Grid container spacing={3} sx={{mb: 3}} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          fontFamily: properties.fontFamily,
                          lineHeight: "1.5",
                          paddingBottom: "4px"
                        }}
                      >
                        KRA Form Edit Deadline <span style={{color: "red"}}>*</span>
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        value={formEditDeadline}
                        onChange={date => {
                          setFormEditDeadline(date);
                          setErrors(prev => ({...prev, form_edit_deadline: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.form_edit_deadline,
                            helperText: errors.form_edit_deadline
                          }
                        }}
                      />
                      <FormHelperText sx={{color: "black", fontWeight: "500"}}>
                        (For Manager)
                      </FormHelperText>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} sx={{mb: 3}} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          fontFamily: properties.fontFamily,
                          lineHeight: "1.5",
                          paddingBottom: "4px"
                        }}
                      >
                        KRA Window For Employee <span style={{color: "red"}}>*</span>
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Open From"
                        value={employeeKraStartDate}
                        onChange={date => {
                          setEmployeeKraStartDate(date);
                          setErrors(prev => ({...prev, employee_kra_start_date: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.employee_kra_start_date,
                            helperText: errors.employee_kra_start_date
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Close On"
                        value={employeeKraEndDate}
                        onChange={date => {
                          setEmployeeKraEndDate(date);
                          setErrors(prev => ({...prev, employee_kra_end_date: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.employee_kra_end_date,
                            helperText: errors.employee_kra_end_date
                          }
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          fontFamily: properties.fontFamily,
                          lineHeight: "1.5",
                          paddingBottom: "4px"
                        }}
                      >
                        KRA Window For Manager <span style={{color: "red"}}>*</span>
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Open From"
                        value={managerKraStartDate}
                        onChange={date => {
                          setManagerKraStartDate(date);
                          setErrors(prev => ({...prev, manager_kra_start_date: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.manager_kra_start_date,
                            helperText: errors.manager_kra_start_date
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Close On"
                        value={managerKraEndDate}
                        onChange={date => {
                          setManagerKraEndDate(date);
                          setErrors(prev => ({...prev, manager_kra_end_date: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.manager_kra_end_date,
                            helperText: errors.manager_kra_end_date
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{p: 3, textAlign: "right", gap: 1, bgcolor: "grey.100"}}>
                  <MDButton
                    variant="gradient"
                    color="warning"
                    sx={{width: "15ch", mr: 2, fontFamily: properties.fontFamily}}
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="#fff"
                    sx={{
                      marginRight: "3px",
                      width: "20ch",
                      background: properties.colors.bgcolorbluethem,
                      fontFamily: properties.fontFamily
                    }}
                    onClick={() => handleSubmit(new Event("submit"))}
                    disabled={isButtonDisabled || loading}
                  >
                    {loading ? "Saving..." : "Save"}
                  </MDButton>
                </Box>
              </Box>
            </Paper>
          </Box>
        </LocalizationProvider>
      </MDBox>

      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnack}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
      >
        <Alert
          onClose={handleCloseSnack}
          severity={snackColor === "success" ? "success" : "error"}
          variant="filled"
          sx={{width: "100%", color: "#fff"}}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default ExtendKra;
